import { ServicePointIdReference } from '../../models/referenceData'
import { FilterParams } from '../../utilities/http-params'
import {
  ActiveFilterDisplay,
  FilterMeta,
  QueryParam,
} from './common'
import { ServicePointGroupWithRefs } from 'src/app/public/service-point-groups/service-point-group.types'

export type ServicePointFilterState = Readonly<{
  servicePoints: readonly ServicePointIdReference[],
  config: {
    meta: FilterMeta,
    allowMultiple?: boolean,
    constant?: boolean
  }
}>

export const initialServicePointFilterState: ServicePointFilterState = {
  servicePoints: [],
  config: {
    meta: {
      fieldName: '',
      fieldGroup: '',
      fieldDisplay: '',
    },
  },
}


export interface SetServicePointFilterConfigAction {
  type: 'SET_SERVICE_POINT_FILTER_CONFIG_ACTION',
  payload: Pick<ServicePointFilterState, 'config'>,
  meta: Omit<ServicePointFilterState['config']['meta'], 'fieldDisplay'>
}
export interface AddServicePointsAction {
  type: 'ADD_SERVICE_POINTS_ACTION',
  payload: Pick<ServicePointFilterState, 'servicePoints'>,
  meta: Omit<ServicePointFilterState['config']['meta'], 'fieldDisplay'>
}
export interface RemoveServicePointsAction {
  type: 'REMOVE_SERVICE_POINTS_ACTION',
  payload: Pick<ServicePointFilterState, 'servicePoints'>,
  meta: Omit<ServicePointFilterState['config']['meta'], 'fieldDisplay'>
}
export interface SetServicePointsAction {
  type: 'SET_SERVICE_POINTS_ACTION',
  payload: Pick<ServicePointFilterState, 'servicePoints'>,
  meta: Omit<ServicePointFilterState['config']['meta'], 'fieldDisplay'>
}

export type ServicePointFilterAction =
  | SetServicePointFilterConfigAction
  | AddServicePointsAction
  | RemoveServicePointsAction
  | SetServicePointsAction

export const servicePointFilterReducer = (state: ServicePointFilterState, action: ServicePointFilterAction): ServicePointFilterState => {
  switch (action.type) {
    case 'SET_SERVICE_POINT_FILTER_CONFIG_ACTION':
      return {
        ...state,
        config: action.payload.config,
      }
    case 'SET_SERVICE_POINTS_ACTION':
      return {
        ...state,
        servicePoints: state.config.allowMultiple
          ? action.payload.servicePoints
          : [action.payload.servicePoints[0]].filter(Boolean),
      }
    case 'REMOVE_SERVICE_POINTS_ACTION':
      return {
        ...state,
        servicePoints: state.servicePoints.filter(ssp => !action.payload.servicePoints.some(asp => asp.service_point_uid === ssp.service_point_uid)),
      }
    case 'ADD_SERVICE_POINTS_ACTION':
      if (state.config.allowMultiple) {
        return {
          ...state,
          servicePoints: [
            ...state.servicePoints.filter(ssp => !action.payload.servicePoints.some(asp => asp.service_point_uid === ssp.service_point_uid)),
            ...action.payload.servicePoints.filter(asp => !state.servicePoints.some(ssp => ssp.service_point_uid === asp.service_point_uid)),
          ],
        }
      }
      return {
        ...state,
        servicePoints: state.servicePoints[0]?.service_point_uid !== action.payload.servicePoints[0]?.service_point_uid
          ? [action.payload.servicePoints[0]].filter(Boolean)
          : [],
      }
    default:
      return state
  }
}

export class ServicePointFilter {
  public readonly type = 'SERVICE_POINT'

  public static SetServicePointFilterConfigAction(
    payload: SetServicePointFilterConfigAction['payload'],
    meta: Omit<ServicePointFilterState['config']['meta'], 'fieldDisplay'>,
  ): SetServicePointFilterConfigAction { return { type: 'SET_SERVICE_POINT_FILTER_CONFIG_ACTION', payload, meta } }
  public static AddServicePointsAction(
    payload: AddServicePointsAction['payload'],
    meta: Omit<ServicePointFilterState['config']['meta'], 'fieldDisplay'>,
  ): AddServicePointsAction { return { type: 'ADD_SERVICE_POINTS_ACTION', payload, meta } }
  public static RemoveServicePointsAction(
    payload: RemoveServicePointsAction['payload'],
    meta: Omit<ServicePointFilterState['config']['meta'], 'fieldDisplay'>,
  ): RemoveServicePointsAction { return { type: 'REMOVE_SERVICE_POINTS_ACTION', payload, meta } }
  public static SetServicePointsAction(
    payload: SetServicePointsAction['payload'],
    meta: Omit<ServicePointFilterState['config']['meta'], 'fieldDisplay'>,
  ): SetServicePointsAction { return { type: 'SET_SERVICE_POINTS_ACTION', payload, meta } }

  public state: ServicePointFilterState
  constructor(
    partialStateWithConfig: Partial<ServicePointFilterState> & Pick<ServicePointFilterState, 'config'>,
    private dispatch: (action: ServicePointFilterAction) => void,
  ) {
    this.state = { ...initialServicePointFilterState, ...partialStateWithConfig }
  }

  public apply(action: ServicePointFilterAction) {
    this.state = servicePointFilterReducer(this.state, action)
    return this
  }

  public asParams(): FilterParams[] {
    return this.state.servicePoints.length
      ? [
        {
          name: this.state.config.meta.fieldName,
          values: [
            {
              operation: 'in=',
              value: [
                this.state.servicePoints.map(sp => sp.service_point_uid),
              ].join(','),
            },
          ],
        },
      ]
      : []
  }

  public asActiveDisplay(): ActiveFilterDisplay[] {
    return this.state.servicePoints.map((sp) => ({
      headerName: this.state.config.meta.fieldDisplay,
      operation: '=',
      value: sp.service_point_id,
      constant: this.state.config.constant ?? false,
      remove: (() =>
        this.dispatch(ServicePointFilter.RemoveServicePointsAction(
          { servicePoints: [sp] },
          this.state.config.meta,
        ))
      ),
    }))
  }

  public asCount() {
    return this.state.servicePoints.length
  }

  public fromUrlQueryParams(
    params: readonly QueryParam[],
    refs: ServicePointIdReference[],
    defaultVal: ServicePointIdReference[] | string | null | undefined,
  ) {
    const qp = params.find((p) => p.name === this.state.config.meta.fieldName)
    let importedState: ServicePointIdReference[] = []
    if (qp?.value) {
      importedState = qp.value
        .split(',')
        .reduce((acc, str) => {
          const foundEntity = refs.find(r => r.service_point_uid === str)
          return foundEntity ? [...acc, foundEntity] : acc
        },
        importedState,
        )
    }

    else if (defaultVal && typeof defaultVal === 'string') {
      importedState = defaultVal.split(',')
        .reduce((acc, str) => {
          // use ids instead of uids in this case only
          const foundEntity = refs.find(r => r.service_point_id === str)
          return foundEntity ? [...acc, foundEntity] : acc
        },
        importedState,
        )
    }

    else if (defaultVal && typeof defaultVal !== 'string') {
      importedState = defaultVal
        .filter((dv) => refs
          .some(r => r.service_point_uid === dv.service_point_uid),
        )
    }

    this.dispatch(ServicePointFilter.SetServicePointsAction(
      { servicePoints: importedState },
      this.state.config.meta,
    ))
  }

  public asUrlQueryParams(): QueryParam[] {
    return this.state.servicePoints.length
      ?
      [{
        name: this.state.config.meta.fieldName,
        value: this.state.servicePoints
          .map((sp) => sp.service_point_uid)
          .join(','),
      }]
      : []
  }

  public asDashboardQueryParams(): QueryParam[] {
    return this.state.servicePoints.length
      ?
      [{
        name: this.state.config.meta.fieldName,
        value: this.state.servicePoints
          .map((sp) => sp.service_point_id)
          .join(','),
      }]
      : []
  }

  asServicePointGroupWithRefs(): Partial<ServicePointGroupWithRefs> {
    const allowed_fields = [
      'service_point_uid',
    ]
    const field = this.state.config.meta.fieldName
    return allowed_fields.includes(field)
      ? { [field]: this.state.servicePoints.map((sp) => sp.service_point_uid) }
      : {}
  }
}
